import React from 'react';
import { useLanguage } from '../../i18n';

// #498 follow-up (PO, binding): PAI never deletes a File Manager document on its
// own. Every transaction-delete confirmation whose rows carry a linked file asks
// the PO's question with ONE explicit opt-in, unticked by default = KEEP the file.
// Render it ONLY when at least one row has a `document_id` (see hasAttachedFile).
// The caller sends `checked` to the REST delete as `delete_files`.
export const hasAttachedFile = (rows) => (rows || []).some((r) => r && r.document_id);

const AttachedFileChoice = ({ checked, onChange, disabled = false, compact = false }) => {
    const { t } = useLanguage();
    return (
        <div
            data-testid="attached-file-choice"
            className={compact
                ? 'mt-2 rounded-lg border border-pai-subtle bg-white/[0.025] p-2 space-y-1.5'
                : 'rounded-ctl border border-pai-subtle bg-white/[0.025] p-3 space-y-2'}
        >
            <p className={compact ? 'text-[11px] leading-[1.5] text-pai-body' : 'text-[13.5px] leading-[1.6] text-pai-body'}>
                {t('finAttachedFileQuestion', 'Do you want to delete the attached file as well, or would you prefer to keep it in the File Manager?')}
            </p>
            <label className={`flex items-center gap-2 cursor-pointer select-none ${compact ? 'text-[11px]' : 'text-[13.5px]'} text-pai-secondary`}>
                <input
                    type="checkbox"
                    data-testid="attached-file-delete-too"
                    checked={!!checked}
                    disabled={disabled}
                    onChange={(e) => onChange(e.target.checked)}
                    className={`${compact ? 'w-3.5 h-3.5' : 'w-4 h-4'} accent-white rounded [color-scheme:dark]`}
                />
                {t('finAttachedFileDeleteToo', 'Delete the attached file as well')}
            </label>
            <p className={compact ? 'text-[10px] text-pai-meta' : 'text-[12.5px] text-pai-faint'}>
                {t('finAttachedFileKeptNote', 'Leave unticked to keep it in the File Manager.')}
            </p>
        </div>
    );
};

// #498 follow-up 2 (PO): deleting a whole book offers to batch-delete the files
// linked to its transactions — the same single opt-in, unticked = KEEP, with the
// count shown so the user knows what they choose. `count` comes from the server
// (GET /books/<id>/linked-files or the chat card's linked_file_count) and already
// leaves out files another book still uses. Render it only when count > 0.
export const LinkedFilesChoice = ({ count, checked, onChange, disabled = false, compact = false }) => {
    const { t } = useLanguage();
    const vars = { n: count, s: count === 1 ? '' : 's' };
    return (
        <div
            data-testid="book-files-choice"
            className={compact
                ? 'mt-2 rounded-lg border border-pai-subtle bg-white/[0.025] p-2 space-y-1.5'
                : 'rounded-ctl border border-pai-subtle bg-white/[0.025] p-3 space-y-2'}
        >
            <p className={compact ? 'text-[11px] leading-[1.5] text-pai-body' : 'text-[13.5px] leading-[1.6] text-pai-body'}>
                {t('finBookFilesQuestion', 'This book\'s transactions have {n} linked file{s} (images or PDFs) in the File Manager.', vars)}
            </p>
            <label className={`flex items-center gap-2 cursor-pointer select-none ${compact ? 'text-[11px]' : 'text-[13.5px]'} text-pai-secondary`}>
                <input
                    type="checkbox"
                    data-testid="book-files-delete-too"
                    checked={!!checked}
                    disabled={disabled}
                    onChange={(e) => onChange(e.target.checked)}
                    className={`${compact ? 'w-3.5 h-3.5' : 'w-4 h-4'} accent-white rounded [color-scheme:dark]`}
                />
                {t('finBookFilesDeleteToo', 'Also delete the {n} linked file{s}', vars)}
            </label>
            <p className={compact ? 'text-[10px] text-pai-meta' : 'text-[12.5px] text-pai-faint'}>
                {t('finBookFilesKeptNote', 'Leave unticked to keep them in the File Manager.')}
            </p>
        </div>
    );
};

export default AttachedFileChoice;
